/// <reference no-default-lib="true" />
/// <reference lib="esnext" />
/// <reference lib="dom" />

export function initFileInputs() {
  const fileInputs = Array.from(document.querySelectorAll('.file-input'))

  for (const fileInput of fileInputs) {
    if (!(fileInput instanceof HTMLElement)) {
      continue
    }

    if (fileInput.dataset.hydrated) {
      continue
    }

    fileInput.dataset.hydrated = 'true'

    const _ = new FileInput(fileInput)
  }
}

type FileInputErrorKind = 'size' | 'accept' | 'count'

type FileInputError = {
  fileName?: string
  kind: FileInputErrorKind
  message: string
}

export class FileInput {
  el: HTMLElement
  filesContainer: HTMLElement
  fileTemplate: HTMLTemplateElement
  input: HTMLInputElement
  errorContainer: HTMLElement
  dt: DataTransfer = new DataTransfer()
  keys: Set<string> = new Set()
  accept: Array<string> = []
  maxSize?: number
  maxCount?: number
  minCount?: number
  acceptError: string
  sizeError: string
  countError: string
  errors: Array<FileInputError> = []

  constructor(el: HTMLElement) {
    this.el = el
    const fileTemplate = el.querySelector<HTMLTemplateElement>(
      '.file-input__file-template',
    )
    const input = el.querySelector<HTMLInputElement>('.file-input__input')
    const filesContainer = el.querySelector<HTMLElement>('.file-input__files')
    const errorContainer = el.querySelector<HTMLElement>('.file-input__error')
    if (!fileTemplate || !input || !filesContainer || !errorContainer) {
      throw new Error('FileInput: required child elements missing')
    }
    this.fileTemplate = fileTemplate
    this.input = input
    this.filesContainer = filesContainer
    this.errorContainer = errorContainer

    this.#addEventListeners.bind(this)()

    this.accept =
      this.el.getAttribute('data-accept')?.split(', ').map((s) => s.trim()) ||
      []
    this.maxSize = this.el.getAttribute('data-max-size')
      ? parseInt(
        this.el.getAttribute('data-max-size')!,
      )
      : undefined
    this.maxCount = this.el.getAttribute('data-max-count')
      ? parseInt(
        this.el.getAttribute('data-max-count')!,
      )
      : Infinity
    this.minCount = this.el.getAttribute('data-min-count')
      ? parseInt(
        this.el.getAttribute('data-min-count')!,
      )
      : 0

    this.acceptError = this.el.getAttribute('data-accept-error') ||
      'File format is not supported.'
    this.sizeError = this.el.getAttribute('data-size-error') ||
      'File is too large.'
    this.countError = this.el.getAttribute('data-count-error') ||
      `Number of files is not correct. Minimum ${this.minCount}, Maximum ${this.maxCount}`
  }

  #addEventListeners() {
    this.input.addEventListener('change', this.#handleChange.bind(this))
  }

  #getFileKey(file: File) {
    return `${file.name}-${file.size}-${file.lastModified}`
  }

  #handleChange() {
    const files = this.input.files

    if (!(files instanceof FileList)) {
      return
    }

    for (const file of Array.from(files)) {
      const key = this.#getFileKey(file)

      if (this.keys.has(key)) {
        continue
      }

      if (this.accept.length) {
        if (!this.accept.includes(file.type)) {
          this.errors.push({
            kind: 'accept',
            fileName: file.name,
            message: this.acceptError,
          })
        }
      }

      if (this.maxSize) {
        if (file.size / (1024 * 1024) > this.maxSize) {
          this.errors.push({
            kind: 'size',
            fileName: file.name,
            message: this.sizeError,
          })
        }
      }

      this.keys.add(key)
      this.dt.items.add(file)
      const fileRendered = this.#renderFile.bind(this)(file)
      this.filesContainer.appendChild(fileRendered)
    }

    this.input.files = this.dt.files
    this.errors = this.errors.filter((error) => error.kind !== 'count')

    if (this.minCount && this.dt.files.length < this.minCount) {
      this.errors.push({ kind: 'count', message: this.countError })
    }

    if (this.maxCount && this.dt.files.length > this.maxCount) {
      this.errors.push({ kind: 'count', message: this.countError })
    }

    if (this.errors.some((error) => error.kind === 'count')) {
      this.errorContainer.innerText = this.countError
    } else {
      this.errorContainer.innerText = ''
    }

    if (this.errors.length > 0) {
      this.input.setCustomValidity(this.errors.map((e) => e.message).join(' '))
      this.el.classList.add('file-input--error')
    } else {
      this.el.classList.remove('file-input--error')
      this.input.setCustomValidity('')
    }
  }

  #removeFile(file: File) {
    const _dt = new DataTransfer()
    const key = this.#getFileKey(file)

    if (!this.input.files) {
      return
    }

    for (
      const existingFile of Array.from(this.dt.files)
    ) {
      if (existingFile.name !== file.name) {
        _dt.items.add(existingFile)
      }
    }

    this.input.files = _dt.files
    this.dt = _dt
    this.filesContainer.querySelector(`[data-key="${key}"]`)
      ?.remove()
    this.keys.delete(key)
    this.errors = this.errors
      .filter((error) => error.fileName !== file.name)
      .filter((error) => error.kind !== 'count')

    if (this.minCount && this.dt.files.length < this.minCount) {
      this.errors.push({ kind: 'count', message: this.countError })
    }

    if (this.maxCount && this.dt.files.length > this.maxCount) {
      this.errors.push({ kind: 'count', message: this.countError })
    }

    if (this.errors.some((error) => error.kind === 'count')) {
      this.errorContainer.innerText = this.countError
    } else {
      this.errorContainer.innerText = ''
    }

    if (this.errors.length > 0) {
      this.input.setCustomValidity(this.errors.map((e) => e.message).join(' '))
      this.el.classList.add('file-input--error')
    } else {
      this.el.classList.remove('file-input--error')
      this.input.setCustomValidity('')
    }
  }

  #renderFile(file: File): DocumentFragment {
    const errors = this.errors.filter((e) => e.fileName === file.name).map(
      (e) => e.message,
    )
    const url = URL.createObjectURL(file)
    const cloned = this.fileTemplate.content.cloneNode(true) as DocumentFragment

    if (errors.length) {
      cloned.querySelector('.file-input__file')?.classList.add(
        'file-input__file--error',
      )
      const errorElement = cloned.querySelector('.file-input__file-error')
      if (errorElement) {
        errorElement.innerHTML = errors.join(' ')
      }
    }

    if (file.type.includes('image') && !errors.length) {
      const image = cloned.querySelector<HTMLImageElement>(
        'img.file-input__file-image',
      )
      if (image) {
        image.src = url
      }
    }

    const fileName = cloned.querySelector<HTMLElement>('.file-input__file-name')
    if (fileName) {
      fileName.innerText = file.name
    }

    const fileSize = cloned.querySelector<HTMLElement>('.file-input__file-size')
    if (fileSize) {
      fileSize.innerText = `(${(file.size / (1024 * 1024)).toFixed(2)} MB)`
    }

    const button = cloned.querySelector<HTMLButtonElement>(
      '.file-input__file-remove',
    )
    if (button) {
      button.addEventListener(
        'click',
        () => this.#removeFile.bind(this)(file),
        { once: true },
      )
    }

    cloned.querySelector('.file-input__file')?.setAttribute(
      'data-key',
      this.#getFileKey(file),
    )

    return cloned
  }
}
